import { baseApi } from "./baseApi";
import { API_ENDPOINTS } from "../../config/url";

// Types for auth API
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  data: {
    _id: string;
    email: string;
    fullName: string;
    token: string;
    tokenType: string;
    [key: string]: any;
  };
  message: string;
  token?: string;
}

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  [key: string]: any;
}

export interface UpdateProfileRequest {
  name?: string;
  email?: string;
  [key: string]: any;
}

export interface SendOtpRequest {
  email: string;
  otpType: number;
}

export interface SendOtpResponse {
  message?: string;
  success?: boolean;
  [key: string]: any;
}

export interface VerifyOtpRequest {
  otpType: number;
  email: string;
  otp: string;
}

export interface VerifyOtpResponse {
  message?: string;
  success?: boolean;
  token?: string;
  [key: string]: any;
}

export interface ResetPasswordRequest {
  isResetPassword: boolean;
  newPassword: string;
}

export interface ResetPasswordResponse {
  message?: string;
  success?: boolean;
  [key: string]: any;
}

export interface ChangePasswordRequest {
  isResetPassword: boolean;
  oldPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  message?: string;
  success?: boolean;
  [key: string]: any;
}

export const authAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // POST - Login
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: API_ENDPOINTS.AUTH.LOGIN,
        method: "POST",
        body: credentials,
        headers: {
          "Content-Type": "application/json",
          "x-portal": "admin" || "subadmin",
        },
      }),
      invalidatesTags: ["AUTH"],
    }),

    // GET - Get User Profile
    getUserProfile: builder.query<UserProfile, void>({
      query: () => API_ENDPOINTS.AUTH.PROFILE,
      providesTags: ["AUTH", "PROFILE"],
    }),

    // PATCH - Update Profile
    updateProfile: builder.mutation<UserProfile, UpdateProfileRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.AUTH.PROFILE,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["AUTH", "PROFILE"],
    }),

    // DELETE - Delete Account
    deleteAccount: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: API_ENDPOINTS.AUTH.ACCOUNT,
        method: "DELETE",
      }),
      invalidatesTags: ["AUTH"],
    }),

    // POST - Send OTP (Forgot Password)
    sendOtp: builder.mutation<SendOtpResponse, SendOtpRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.AUTH.SEND_OTP,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["AUTH"],
    }),

    // POST - Verify OTP
    verifyOtp: builder.mutation<VerifyOtpResponse, VerifyOtpRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.AUTH.VERIFY_OTP,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["AUTH"],
    }),

    // POST - Reset Password (Change Password)
    resetPassword: builder.mutation<ResetPasswordResponse, ResetPasswordRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["AUTH"],
    }),

    // POST - Change Password (with old password)
    changePassword: builder.mutation<ChangePasswordResponse, ChangePasswordRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["AUTH"],
    }),

    // GET - Logout
    logout: builder.mutation<{ message?: string; success?: boolean }, void>({
      query: () => ({
        url: API_ENDPOINTS.AUTH.LOGOUT,
        method: "GET",
      }),
      invalidatesTags: ["AUTH"],
    }),
  }),
});

export const {
  useLoginMutation,
  useGetUserProfileQuery,
  useUpdateProfileMutation,
  useDeleteAccountMutation,
  useSendOtpMutation,
  useVerifyOtpMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useLogoutMutation,
} = authAPI;

